import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Card, ConfigProvider, Segmented, Spin, Tooltip } from 'antd'
import type { Dayjs } from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'

import CreatorEarningSummary from '@/components/shared/creator-earning-summary'
import { Period, PeriodOptions } from '@/constants/calendar-period'
import { useFormatTime } from '@/hooks/use-format-time'
import {
  ChatterSalesDetail,
  CreatorOverviewSummary,
  TopCreatorsSummary,
} from '@/model/analytics'
import { readChatterOverviewSales } from '@/redux/chatter-reports-actions'
import { readAllCreators } from '@/redux/creator-actions'
import { readCreatorOverviewSummary, readTopCreators } from '@/redux/creator-reports'
import { selectFeatureTimezone } from '@/redux/global-selector'
import { useAppDispatch, useAppSelector } from '@/redux/utils'
import { dayjs } from '@/utils/dayjs'

import ChatterSalesReport from './components/chatter-sales-report'
import RevenueDistributionChart from './components/revenue-distribution-chart'
import TopCreators from './components/top-creators'
import s from './index.module.css'

const segmentedTheme = {
  token: {
    borderRadius: 4,
    borderRadiusSM: 2,
    controlPaddingHorizontal: 22,
  },
  components: {
    Segmented: {
      fontSize: 12,
      trackBg: 'var(--ant-color-bg-base)',
      itemSelectedColor: 'var(--ant-color-primary)',
      itemSelectedBg: 'var(--ant-color-primary-bg)',
      itemHoverBg: 'var(--ant-color-primary-bg-hover)',
      itemHoverColor: 'var(--ant-color-primary-hover)',
      itemActiveBg: 'var(--ant-color-primary-bg)',
    },
  },
}

export default function Home() {
  const { featureTimezoneName } = useAppSelector(selectFeatureTimezone)
  const formatTime = useFormatTime()
  const defaultRange = [
    dayjs().tz(featureTimezoneName).startOf('week'),
    dayjs().tz(featureTimezoneName).endOf('week'),
  ]
  const [creatorOverviewFetching, setCreatorOverviewFetching] = useState(false)
  const [topCreatorsFetching, setTopCreatorsFetching] = useState(false)
  const [chatterOverviewFetching, setChatterOverviewFetching] = useState(false)
  const [dateRange, setDateRange] = useState<Dayjs[]>(defaultRange)
  const [creatorOverview, setCreatorOverview] = useState<CreatorOverviewSummary | undefined>()
  const [topCreatorSummary, setTopCreatorSummary] = useState<TopCreatorsSummary | undefined>()
  const [chatterSalesDetails, setChatterSalesDetails] = useState<ChatterSalesDetail[]>([])

  const dispatch = useAppDispatch()

  useEffect(() => {
    void dispatch(readAllCreators())
  }, [dispatch])

  useEffect(() => {
    const fetchChatterReport = async () => {
      try {
        setCreatorOverviewFetching(true)
        setChatterOverviewFetching(true)
        const requestParams = { startAt: dateRange[0].toDate(), endAt: dateRange[1].toDate() }
        const [creatorOverviewResult, chatterSalesResult] =
          await Promise.allSettled([
            dispatch(readCreatorOverviewSummary(requestParams)).unwrap(),
            dispatch(readChatterOverviewSales({
              period: 'Day',
              ...requestParams,
            })).unwrap(),
          ])

        if (creatorOverviewResult.status === 'fulfilled') {
          setCreatorOverview(creatorOverviewResult.value)
          setCreatorOverviewFetching(false)
        }
        if (chatterSalesResult.status === 'fulfilled') {
          setChatterSalesDetails(chatterSalesResult.value.details)
          setChatterOverviewFetching(false)
        }
      } catch (error) {
        console.error('error fetching chatter reports:', error)
      } finally {
        setChatterOverviewFetching(false)
      }
    }
    void fetchChatterReport()
  }, [dateRange, dispatch])

  useEffect(() => {
    const fetchTopCreators = async () => {
      try {
        setTopCreatorsFetching(true)
        const requestParams = { startAt: dateRange[0].toDate(), endAt: dateRange[1].toDate() }
        const result = await dispatch(readTopCreators(requestParams)).unwrap()
        setTopCreatorSummary(result)
      } catch (error) {
        console.error('error fetching top creators:', error)
      } finally {
        setTopCreatorsFetching(false)
      }
    }
    void fetchTopCreators()
  }, [dateRange, dispatch])

  const handlePeriodFilterChange = useCallback((value: Period) => {
    const zonedNow = dayjs().tz(featureTimezoneName)
    let startAt, endAt
    if (value === Period.Yesterday) {
      const yesterday = zonedNow.subtract(1, 'day')
      startAt = yesterday.startOf('day')
      endAt = yesterday.endOf('day')
    } else if (value === Period.Today) {
      startAt = zonedNow.startOf('day')
      endAt = zonedNow.endOf('day')
    } else if (value === Period.ThisMonth) {
      startAt = zonedNow.startOf('month')
      endAt = zonedNow.endOf('month')
    } else {
      startAt = zonedNow.startOf('week')
      endAt = zonedNow.endOf('week')
    }
    setDateRange([startAt, endAt])
  }, [featureTimezoneName])

  return (
    <div className={s.Home}>
      <Card
        title={
          <div>
            Creator earnings overview
            <Tooltip title="An overview of total earnings based on your managed Creators during this period">
              <ExclamationCircleOutlined />
            </Tooltip>
            <span className={s.TimeZone}>UTC{formatTime({ format: 'Z' })(dayjs())}</span>
          </div>
        }
        className={s.CreatorEarningOverview}
        variant='borderless'
        extra={
          <ConfigProvider theme={segmentedTheme}>
            <Segmented
              style={{ border: 'solid 1px var(--ant-color-border)' }}
              options={PeriodOptions}
              defaultValue={Period.ThisWeek}
              onChange={handlePeriodFilterChange}
            />
          </ConfigProvider>
        }
      >
        <Spin spinning={creatorOverviewFetching}>
          <CreatorEarningSummary
            totalEarnings={creatorOverview?.grossEarning.total}
            subscriptionsEarning={creatorOverview?.grossEarning.subscription}
            tipsEarning={creatorOverview?.grossEarning.tip}
            postsEarning={creatorOverview?.grossEarning.post}
          />
        </Spin>
      </Card>
      <div className={s.BottomGrid}>
        <div className={s.BottomLeftGrid}>
          <Card
            className={s.TopCreators}
            variant='borderless'
            title={
              <div>
                Top creators by revenue
              </div>
            }
          >
            <Spin spinning={topCreatorsFetching}>
              <TopCreators creatorRevenues={topCreatorSummary?.grossRevenue} />
            </Spin>
          </Card>
        </div>
        <div className={s.BottomRightGrid}>
          <Card
            className={s.RevenueDistribution}
            variant='borderless'
            title="Revenue distribution by category"
          >
            <Spin spinning={creatorOverviewFetching}>
              <RevenueDistributionChart data={creatorOverview?.grossEarning} />
            </Spin>
          </Card>
          <Card
            title={
              <div>
                Employee sale
                <Tooltip title="Chatter sales growth and trends determined by your team's sales settings">
                  <ExclamationCircleOutlined />
                </Tooltip>
              </div>
            }
            className={s.ChatterSales}
            variant='borderless'
          >
            <Spin spinning={chatterOverviewFetching}>
              <ChatterSalesReport data={chatterSalesDetails} />
            </Spin>
          </Card>
        </div>
      </div>
    </div >
  )
}
